import React, { useMemo, useState } from 'react';

import { sortBy } from 'lodash';
import { Controller, ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

import { useDebounce } from 'hooks/useDebaunce';

import { useQueryGetMilestones } from 'api/queries/getPhasePerformanceMilestones.query';

import { Select, SelectProps } from 'components/DesignSystem/Select';
import { milestoneMapper } from 'components/pages/PhasePerformanceManagePage/components/Manage/Manage.utils';

type ActivitiesSelectProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> & Omit<SelectProps, 'name' | 'options'> & {
    contractId: number | undefined;
};

export const ActivitiesSelect = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
    contractId,
    ...selectProps
}: ActivitiesSelectProps<TFieldValues, TName>) => {
    const [filter, setFilter] = useState('');
    const debouncedFilter = useDebounce<string>(filter, 1000);

    const { data: milestonesData, isFetching } = useQueryGetMilestones({ contractId, filter: debouncedFilter });

    const options = useMemo(
        () => sortBy(milestonesData, 'calculated_date').map(milestoneMapper),
        [milestonesData],
    );

    const handleInputChange = (inputText: string, { action }: { action: string }) => {
        if (action !== 'input-blur' && action !== 'menu-close' && action !== 'set-value') {
            setFilter(inputText);
        }
        if (action === 'menu-close') {
            setFilter('');
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            shouldUnregister={shouldUnregister}
            render={({ field }) => (
                <Select
                    {...field}
                    {...selectProps}
                    options={options}
                    isLoading={isFetching}
                    onInputChange={handleInputChange}
                />
            )}
        />
    );
};
